import {
  Box,
  Group,
  HoverCard,
  ScrollArea,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { StatusBadge } from '../common/StatusBadge';
import { ComponentProjectFinancials, ComponentProjectFinancialsItem } from '../../types/financials';

const formatCurrency = (n: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);

const TOOLTIP =
  'Estimated federal giveback if the grant ended today: the gap between federal and match spend rates, applied to the federal budget.';

function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

export function isSubawardeeAtGivebackRisk(
  item: Pick<ComponentProjectFinancialsItem, 'givebackRisk'>
): boolean {
  return isFiniteNumber(item.givebackRisk) && item.givebackRisk > 0;
}

export type GivebackAtRiskSubawardee = {
  subawardeeId: string;
  dollars: number;
};

export type GivebackAtRiskSummary = {
  count: number;
  dollars: number;
  subawardees: GivebackAtRiskSubawardee[];
};

export type GivebackAtRiskNamedSubawardee = {
  id: string;
  name: string;
  dollars: number;
};

export const UNKNOWN_GIVEBACK_SUBAWARDEE_ID = '__unknown_subawardee__';

function normalizeAtRiskSubawardeeId(subawardeeId: string | null | undefined): string {
  if (typeof subawardeeId !== 'string') return UNKNOWN_GIVEBACK_SUBAWARDEE_ID;
  const trimmed = subawardeeId.trim();
  return trimmed.length > 0 ? trimmed : UNKNOWN_GIVEBACK_SUBAWARDEE_ID;
}

/** Resolve display names for at-risk rows; never returns a blank label. */
export function nameGivebackAtRiskSubawardees(
  atRisk: GivebackAtRiskSummary,
  labelById: Record<string, string>,
  resolveLabel: (
    subawardeeId: string | null | undefined,
    labelById: Record<string, string>
  ) => string | null
): GivebackAtRiskNamedSubawardee[] {
  return atRisk.subawardees.map((entry) => {
    const label = resolveLabel(entry.subawardeeId, labelById);
    const trimmedLabel = label?.trim() ?? '';
    const trimmedId = entry.subawardeeId.trim();
    const name =
      trimmedLabel ||
      (trimmedId === UNKNOWN_GIVEBACK_SUBAWARDEE_ID || !trimmedId
        ? 'Unknown sub-awardee'
        : trimmedId);
    return {
      id: entry.subawardeeId,
      name,
      dollars: entry.dollars,
    };
  });
}

/** Unique subawardees with giveback risk > 0, and sum of their giveback dollars. */
export function summarizeGivebackAtRisk(
  items: Array<Pick<ComponentProjectFinancialsItem, 'givebackRisk' | 'subawardeeId'>>
): GivebackAtRiskSummary {
  const dollarsById = new Map<string, number>();
  for (const item of items) {
    if (!isSubawardeeAtGivebackRisk(item)) continue;
    const subawardeeId = normalizeAtRiskSubawardeeId(item.subawardeeId);
    dollarsById.set(subawardeeId, (dollarsById.get(subawardeeId) ?? 0) + item.givebackRisk);
  }

  const subawardees = Array.from(dollarsById.entries())
    .map(([subawardeeId, dollars]) => ({ subawardeeId, dollars }))
    .sort(
      (a, b) => b.dollars - a.dollars || a.subawardeeId.localeCompare(b.subawardeeId)
    );

  return {
    count: subawardees.length,
    dollars: subawardees.reduce((sum, entry) => sum + entry.dollars, 0),
    subawardees,
  };
}

export function summarizeProjectGivebackAtRisk(
  projects: ComponentProjectFinancials[]
): GivebackAtRiskSummary {
  return summarizeGivebackAtRisk(projects.flatMap((project) => project.items));
}

export type GivebackRiskIndicatorProps = {
  /** Giveback dollars from the API. Only rendered when > 0 (unless reserveSpace). */
  value: number | null;
  /**
   * When true, always occupies a footer-row height so sibling cards stay aligned
   * even when this one has no giveback risk.
   */
  reserveSpace?: boolean;
};

/**
 * Giveback cue using the same StatusBadge as past-due report cards.
 */
export function GivebackRiskIndicator({
  value,
  reserveSpace = false,
}: GivebackRiskIndicatorProps) {
  const show = isFiniteNumber(value) && value > 0;
  const label = show ? `Giveback ${formatCurrency(value)}` : null;

  const content = show ? (
    <Box onClick={(event) => event.stopPropagation()} sx={{ width: 'fit-content' }}>
      <StatusBadge status="past_due" label={label!} tooltipLabel={TOOLTIP} />
    </Box>
  ) : null;

  if (!reserveSpace) return content;

  return (
    <Box
      sx={{
        minHeight: 32,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {content}
    </Box>
  );
}

/** @deprecated Prefer GivebackRiskIndicator */
export const GivebackRiskBadge = GivebackRiskIndicator;

export type SubawardeesAtRiskIndicatorProps = {
  count: number;
  dollars?: number | null;
};

/** Count cue using the same StatusBadge as past-due report cards. */
export function SubawardeesAtRiskIndicator({
  count,
  dollars,
}: SubawardeesAtRiskIndicatorProps) {
  if (count <= 0) return null;

  const label =
    count === 1 ? '1 sub-awardee at risk' : `${count} sub-awardees at risk`;
  const tip =
    isFiniteNumber(dollars) && dollars > 0
      ? `${label}. Combined giveback ${formatCurrency(dollars)}.`
      : `${label}. Federal spend is outpacing match.`;

  return (
    <Box onClick={(event) => event.stopPropagation()} sx={{ width: 'fit-content' }}>
      <StatusBadge status="past_due" label={label} tooltipLabel={tip} />
    </Box>
  );
}

/** @deprecated Prefer SubawardeesAtRiskIndicator */
export const SubawardeesAtRiskBadge = SubawardeesAtRiskIndicator;

export type GivebackAtRiskOverviewCardProps = {
  subawardeeCount: number;
  dollars: number;
  /** When provided, hovering the at-risk count shows a scrollable name + $ list. */
  atRiskSubawardees?: GivebackAtRiskNamedSubawardee[];
  /** Full-width strip under spend tiles; default is a square metric card. */
  variant?: 'tile' | 'banner';
};

/** Consortium-level overview: dollars first, subawardee count secondary. */
export function GivebackAtRiskOverviewCard({
  subawardeeCount,
  dollars,
  atRiskSubawardees = [],
  variant = 'tile',
}: GivebackAtRiskOverviewCardProps) {
  const hasRisk = subawardeeCount > 0 && dollars > 0;
  const theme = useMantineTheme();
  const countLabel = `${subawardeeCount} sub-awardee${subawardeeCount === 1 ? '' : 's'} at risk`;
  const showNamesCard = hasRisk && atRiskSubawardees.length > 0;

  const countText = showNamesCard ? (
    <HoverCard
      position="bottom-start"
      withArrow
      withinPortal
      shadow="md"
      width={280}
      openDelay={100}
      closeDelay={150}
    >
      <HoverCard.Target>
        <Text
          size="xs"
          aria-label={`${countLabel}. Hover for sub-awardee names.`}
          sx={{
            cursor: 'help',
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
            textUnderlineOffset: 2,
            width: 'fit-content',
            color: theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.gray[6],
            '&:hover': {
              color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[8],
            },
          }}
        >
          {countLabel}
        </Text>
      </HoverCard.Target>
      <HoverCard.Dropdown p={0}>
        <Box px="sm" pt="sm" pb={6}>
          <Text size="xs" weight={700}>
            At-risk sub-awardees
          </Text>
        </Box>
        <ScrollArea
          type="scroll"
          offsetScrollbars
          sx={{ maxHeight: 220, paddingBottom: 4 }}
        >
          <Stack spacing={0} px="sm" pb="sm">
            {atRiskSubawardees.map((entry, index) => (
              <Group
                key={`${entry.id}-${index}`}
                position="apart"
                noWrap
                spacing="sm"
                sx={(t) => ({
                  paddingTop: 6,
                  paddingBottom: 6,
                  borderTop:
                    index === 0
                      ? 'none'
                      : `1px solid ${
                          t.colorScheme === 'dark' ? t.colors.dark[4] : t.colors.gray[2]
                        }`,
                })}
              >
                <Text size="xs" lineClamp={2} sx={{ minWidth: 0, flex: '1 1 auto' }}>
                  {entry.name || 'Unknown sub-awardee'}
                </Text>
                <Text
                  size="xs"
                  weight={600}
                  sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
                >
                  {formatCurrency(entry.dollars)}
                </Text>
              </Group>
            ))}
          </Stack>
        </ScrollArea>
      </HoverCard.Dropdown>
    </HoverCard>
  ) : (
    <Text size="xs" color="dimmed">
      {hasRisk ? countLabel : 'No sub-awardees currently at giveback risk'}
    </Text>
  );

  const amount = (
    <Text
      weight={700}
      sx={{
        fontSize: variant === 'banner' ? 22 : 28,
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
        color: hasRisk ? theme.colors.red[6] : undefined,
      }}
    >
      {formatCurrency(dollars)}
    </Text>
  );

  return (
    <Box
      p="md"
      sx={(theme) => ({
        borderRadius: theme.radius.md,
        backgroundColor:
          theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        border: `1px solid ${
          theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
        }`,
        height: variant === 'tile' ? '100%' : undefined,
      })}
    >
      {variant === 'banner' ? (
        <Group position="apart" align="center" noWrap spacing="md">
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <Text size="md" weight={700}>
              Giveback Exposure
            </Text>
            {countText}
          </Stack>
          {amount}
        </Group>
      ) : (
        <Stack spacing="sm">
          <Text size="md" weight={700}>
            Giveback Exposure
          </Text>
          {amount}
          {countText}
        </Stack>
      )}
    </Box>
  );
}
